/**
 * Canvas→bytes plumbing for the travel arche's trip export (JPG + PDF
 * paths in `TravelExportImport.tsx`), extracted as a pure seam so the
 * encode contract is unit-testable (M5 review #16 — the component itself
 * has no tests; the html2canvas capture + jsPDF sizing glue stays there).
 *
 * Failure messages are the component's legacy user-facing strings — they
 * surface verbatim in the export-failed toast and `captureError`
 * telemetry.
 */

import { blobToUint8Array, canvasToBlob, CanvasEncodeError } from '@fias/canvas-kit';

/** Export encode quality — the pre-extraction inline value. */
export const TRIP_EXPORT_JPEG_QUALITY = 0.92;

async function encodeTripJpeg(canvas: HTMLCanvasElement, failureMessage: string): Promise<Blob> {
  try {
    return await canvasToBlob(canvas, 'image/jpeg', TRIP_EXPORT_JPEG_QUALITY);
  } catch (err) {
    if (err instanceof CanvasEncodeError) {
      throw new Error(failureMessage);
    }
    throw err;
  }
}

/** JPG download/upload path: canvas → JPEG blob. */
export function captureCanvasAsJpegBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return encodeTripJpeg(canvas, 'Failed to create JPG');
}

/** PDF path: canvas → JPEG bytes for `jsPDF.addImage` (bytes rather than a
 *  base64 data URL — huge canvases can fail data-URL stringification). */
export async function canvasToPdfImageBytes(canvas: HTMLCanvasElement): Promise<Uint8Array> {
  const jpegBlob = await encodeTripJpeg(canvas, 'Failed to create PDF image');
  return blobToUint8Array(jpegBlob);
}
